function getConfig(name, defaultValue = null) {
    // If inside a docker container, use window.ENV
    if (window.ENV !== undefined) {
        return window.ENV[name] || defaultValue;
    }

    return process.env[name] || defaultValue;
}

const IPV4_REGEX = /^\d{1,3}(\.\d{1,3}){3}$/;

const normalizeHost = (h) =>
    String(h || "").trim().toLowerCase().replace(/^\.+/, "").replace(/\.+$/, "");

const currentHostname = () =>
    normalizeHost(
        (typeof window !== "undefined" && window.location && window.location.hostname) || ""
    );

const hostFromUrl = (url) => {
    if (!url) return "";
    try {
        return normalizeHost(new URL(String(url).trim()).hostname);
    } catch (e) {
        return "";
    }
};

/**
 * Domínio base para subdomínios de tenant (`slug.BASE_DOMAIN`) — Revenda.
 * `REACT_APP_BASE_DOMAIN` → fallback: hostname atual sem o primeiro label.
 * (Mora aqui, e não em utils/tenant.js, para evitar import circular; tenant.js reexporta.)
 */
export function getBaseDomain() {
    const fromEnv = normalizeHost(getConfig("REACT_APP_BASE_DOMAIN", ""));
    if (fromEnv) return fromEnv;

    const host = currentHostname();
    if (!host || host === "localhost" || IPV4_REGEX.test(host)) return host;

    const labels = host.split(".");
    if (labels.length <= 2) return host;
    return labels.slice(1).join(".");
}

/**
 * `true` quando o front está sendo servido pelo host da própria plataforma
 * (`app.BASE_DOMAIN`, host de `REACT_APP_FRONTEND_URL`, `localhost` ou IP).
 * Nesses casos a API é `REACT_APP_BACKEND_URL` (comportamento original).
 * Em qualquer outro host (subdomínio `slug.BASE_DOMAIN` ou domínio próprio de
 * um tenant) a API passa a ser same-origin: `${origin}/backend`.
 *
 * Sem `REACT_APP_BASE_DOMAIN` e sem `REACT_APP_FRONTEND_URL` configurados não há
 * como saber qual é o host da plataforma → assume plataforma (modo Revenda desligado),
 * o que preserva o comportamento atual em instalações antigas.
 */
export function isPlatformHost() {
    const host = currentHostname();
    if (!host || host === "localhost" || host === "127.0.0.1" || IPV4_REGEX.test(host)) {
        return true;
    }

    const frontendHost = hostFromUrl(getConfig("REACT_APP_FRONTEND_URL", ""));
    if (frontendHost && host === frontendHost) return true;

    const baseFromEnv = normalizeHost(getConfig("REACT_APP_BASE_DOMAIN", ""));
    if (!baseFromEnv && !frontendHost) return true;

    const base = getBaseDomain();
    if (base && host === `app.${base}`) return true;

    return false;
}

/** URL base da API: `REACT_APP_BACKEND_URL` na plataforma; `${origin}/backend` em domínio de tenant. */
export function getBackendUrl() {
    if (isPlatformHost()) {
        return getConfig('REACT_APP_BACKEND_URL');
    }
    return `${window.location.origin}/backend`;
}

/** `path` do socket.io: `/socket.io` na plataforma; `/backend/socket.io` em domínio de tenant. */
export function getSocketPath() {
    return isPlatformHost() ? "/socket.io" : "/backend/socket.io";
}

/**
 * URL do socket.io da empresa. O socket.io-client usa o *caminho* da URL como namespace,
 * então em domínio de tenant NÃO se pode usar `${getBackendUrl()}/${companyId}`
 * (viraria namespace `/backend/46`, que o servidor recusa — o `/backend` fica só no `path`).
 * Namespace tem de ser sempre `/${companyId}`.
 */
export function getSocketUrl(companyId) {
    if (isPlatformHost()) {
        return `${getBackendUrl()}/${companyId}`;
    }
    return `${window.location.origin}/${companyId}`;
}

export function getHoursCloseTicketsAuto() {
    return getConfig('REACT_APP_HOURS_CLOSE_TICKETS_AUTO');
}

export function getFrontendPort() {
    return getConfig('SERVER_PORT');
}

export function getPrimaryColor() {
    return getConfig("REACT_APP_PRIMARY_COLOR");
}

export function getPrimaryDark() {
    return getConfig("REACT_APP_PRIMARY_DARK");
}

export function getNumberSupport() {
    return getConfig("REACT_APP_NUMBER_SUPPORT");
}
